import { useCallback, useState } from 'react';
import { useCancelJobMutation } from '~/domain/Jobs';

export interface IUseCancelShiftPopupResult {
  /** Whether the cancel confirmation is showing. */
  isOpen: boolean;
  /** Shows the confirmation. */
  open: () => void;
  /** Dismisses the confirmation, unless the cancellation is already in flight. */
  close: () => void;
  /** Cancels the shift and leaves for the cancellation flow. */
  confirm: () => void;
  /** Whether the cancellation request is in flight. */
  isCancelling: boolean;
}

/**
 * The cancel confirmation's open state and its request, for a Shift Details page
 * that offers Cancel. Each page keeps its own popup — a booked shift's discloses
 * the charge and refund, an open shift's has no money to show — and wires it to
 * this.
 *
 * The popup is held open while the request is in flight, so the office sees it
 * working and cannot dismiss it and cancel a second time. Success needs no close:
 * `redirect: true` leaves the page for the cancellation flow, as the legacy popup
 * does (only the list card cancels in place). A failure closes it, because the
 * mutation already reports the error and the popup only has to get out of the way.
 */
export const useCancelShiftPopup = (jobId: string): IUseCancelShiftPopupResult => {
  const { mutate: cancelJob, isPending: isCancelling } = useCancelJobMutation();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);

  const close = useCallback(() => {
    if (!isCancelling) {
      setIsOpen(false);
    }
  }, [isCancelling]);

  const confirm = useCallback(
    () => cancelJob({ jobId, redirect: true }, { onError: () => setIsOpen(false) }),
    [cancelJob, jobId],
  );

  return { isOpen, open, close, confirm, isCancelling };
};
